<script setup>
import { ref, onMounted, computed } from 'vue'
import Main from './Main.vue'
import { Icon, Editable, ProfilePicture } from '@components'
import { getPrice, showDurationUnit } from '@utils'
import draggable from "vuedraggable"


import { useCustomizer } from '@store/customizer'
const $customizer = useCustomizer()

import { useDeviceLayout } from '../useDeviceLayout'
const $deviceLayout = useDeviceLayout()

import { useService } from '@store/service.js'
const $service = useService()

import { useCategory } from '@store/category.js'
const $category = useCategory()

import { useSettings } from '@store/settings'
const $settings = useSettings()

const pageDetails = ref({
    layoutTitle: `${$settings.labels.service?.singular} Selection`,
    // frontendTitle: 'Select Service'
})

onMounted(() => {
    $category.fetchCategories()
    $service.fetchData()
})

const sortedServices = computed(() => {
    let services = []

    // find only published service
    let ser = $service.getServices.filter((service) => service.status == 'publish' && !service.meta.is_private) || []
    ser.forEach(s => services.push(s))

    // return services
    // let services = $service.getServices || []

    const catPriority = (catId) => {
        return $category.getCategories.find(cat => cat.category_id === catId)?.meta?.priority || 0
    }

    // Sort services by category_id and then by order within each category
    services.sort((a, b) => {
        if (catPriority(a.category_id) < catPriority(b.category_id)) {
            return -1
        } else if (catPriority(a.category_id) > catPriority(b.category_id)) {
            return 1
        } else {
            // If category_id is the same, sort by 'order'
            return a.order - b.order
        }
    })

    return services


})

const serviceChanged = (event) => {
    sortedServices.value.forEach((service, index) => {
        if (service.meta.order !== index) {
            service.meta.order = index
            $service.updateService(service, false)
        }
    })
}

const prevY = ref(0)
const scrollContainer = ref(null)

const changeViewport = (event) => {
    const scrollSpeed = 3
    const currentY = event.clientY
    const containerRect = scrollContainer.value.getBoundingClientRect()

    if (!(currentY >= containerRect.bottom - 100 || currentY <= containerRect.top + 100)) return

    if (currentY > prevY.value || currentY >= containerRect.bottom - 80) {
        // Scroll down
        scrollContainer.value.scrollBy(0, scrollSpeed)
    } else if (currentY < prevY.value || currentY <= containerRect.top + 80) {
        // Scroll up
        scrollContainer.value.scrollBy(0, -scrollSpeed)
    }

    prevY.value = currentY
}

</script>

<template>
    <Main :pageTitle="pageDetails.layoutTitle" @submit="$customizer.publish" :next="{ name: $settings.addons.staffs ? 'customizer.form.staff' : 'customizer.form.date' }">
        <Editable v-model="customizer.service.title" :iconVisibility="false" border inputClass="text-xl" :inputStyle="$customizer.titleStyle"></Editable>

        <div ref="scrollContainer" class="h-[420px] overflow-auto mt-5">
            <!-- row layout -->
            <draggable v-if="$customizer.getMenuStatusById('service', 'layout_type') == 'row01'" :list="sortedServices" item-key="service_id" @end="serviceChanged" ghost-class="ghost" emptyInsertThreshold="100" class="flex flex-col gap-3">
                <template #item="{ element: service, index }">
                    <div @drag="changeViewport" @dragend="prevY = 0" class="cursor-move flex gap-3 items-center justify-between p-3 border border-[#D0D5DD]" :class="{ 'rounded-lg': $customizer.isRounded }">
                        <div class="flex gap-3 items-center justify-center">
                            <Icon name="dotsMove" size="20px" class="text-[#98A2B3]"></Icon>
                            <ProfilePicture v-if="$customizer.getMenuStatusById('service', 'show_image')" :name="service.name" :img="service.meta?.thumbnail_image_url" :bgColor="service.meta?.color" :textClass="`!w-[80px] !h-[60px] ${$customizer.isRounded ? '!rounded' : '!rounded-none'}`" :imgClass="`!w-[80px] !h-[60px] ${$customizer.isRounded ? '!rounded' : '!rounded-none'}`" />
                            <div>
                                <div class="text-[#101828] text-base font-semibold truncate" :class="[$deviceLayout.isMobile ? 'w-[45px]' : 'w-[200px]']" :style="$customizer.subtitleStyle">
                                    {{ service.name }}
                                </div>
                                <div v-if="$customizer.getMenuStatusById('service', 'show_price') && service.meta.price_type != 'none'" class="text-[#475467] text-sm">
                                    {{ service.meta.price_type == 'free' ? 'Free' : getPrice(service.meta?.price) }}
                                </div>
                            </div>
                        </div>
                        <div class="flex items-center justify-center gap-3">
                            <div v-if="$customizer.getMenuStatusById('service', 'show_hours')" class="text-[#101828] text-base font-semibold" :style="$customizer.subtitleStyle">
                                {{ showDurationUnit(service.meta.duration, service.meta.duration_unit) || '0 min' }}
                            </div>
                        </div>
                    </div>
                </template>
            </draggable>

            <!-- grid layout -->
            <draggable v-if="$customizer.getMenuStatusById('service', 'layout_type') == 'grid01'" :list="sortedServices" item-key="service_id" @end="serviceChanged" ghost-class="ghost" emptyInsertThreshold="100" class="grid gap-3" :class="[$deviceLayout.isMobile ? 'grid-cols-1' : 'grid-cols-2']">
                <template #item="{ element: service, index }">
                    <div @drag="changeViewport" @dragend="prevY = 0" class="flex gap-3 items-center p-3 border border-[#D0D5DD]" :class="{ 'rounded-lg': $customizer.isRounded }">
                        <Icon name="dotsMove" size="20px" class="text-[#98A2B3]"></Icon>
                        <ProfilePicture v-if="$customizer.getMenuStatusById('service', 'show_image')" :name="service.name" :img="service.meta?.thumbnail_image_url" :bgColor="service.meta?.color" :textClass="`!w-[88px] !h-[66px] ${$customizer.isRounded ? '!rounded' : '!rounded-none'}`" :imgClass="`!w-[88px] !h-[66px] ${$customizer.isRounded ? '!rounded' : '!rounded-none'}`" />
                        <div class="flex flex-col h-full" :class="{ 'justify-between': $customizer.getMenuStatusById('service', 'show_price') && service.meta.price_type != 'none' }">
                            <div class="text-[#101828] text-base font-semibold truncate" :class="{ 'w-[120px]': $deviceLayout.isMobile }, { 'w-[45px]': $deviceLayout.isTablet }, { 'w-[95px]': $deviceLayout.isDesktop }" :style="$customizer.subtitleStyle">
                                {{ service.name }}
                            </div>
                            <div v-if="$customizer.getMenuStatusById('service', 'show_hours')" class="text-[#101828] text-xs" :style="$customizer.subtitleStyle" :class="{ 'mt-[3px]': !$customizer.getMenuStatusById('service', 'show_price') || service.meta.price_type == 'none' }">
                                {{ showDurationUnit(service.meta.duration, service.meta.duration_unit) || '0 min' }}
                            </div>
                            <div v-if="$customizer.getMenuStatusById('service', 'show_price') && service.meta.price_type != 'none'" class="text-[#475467] text-sm">
                                {{ service.meta.price_type == 'free' ? 'Free' : getPrice(service.meta?.price) }}
                            </div>
                        </div>
                    </div>
                </template>
            </draggable>
        </div>
    </Main>
</template>